import { queryOptions, useQuery } from '@tanstack/react-query'
import { QueryCacheKeys } from '@/api/queryKeys.ts'
import { apiGet } from '@/api/api.ts'
import { availabilityStaleTime } from '@/util/positionAvailability.ts'

// 하루의 위치 기록 수. 위치를 확인한 기록과 확인하지 못한 기록을 따로 센다.
export interface IUserPositionDay {
  date: string
  located: number
  unavailable: number
}

export interface IUserMonthAvailability {
  year: number
  month: number
  days: IUserPositionDay[]
}

// 한 사용자의 한 달 기록. 응답에 요청한 사용자·연·월을 붙여, 어느 달의 기록인지 알 수 있고
// 사용자가 바뀐 뒤에 이전 사용자의 기록이 남아 보이지 않게 한다.
export interface IUserMonthDays extends IUserMonthAvailability {
  userId: number
}

// 한 달 중 위치 기록이 있는 날. 기록이 없는 날은 들어 있지 않다.
export const fetchUserMonthDays = async (userId: number, year: number, month: number): Promise<IUserMonthDays> => {
  const response = await apiGet<IUserMonthAvailability>(
    `/protectee/${userId}/monthly-position/availability?year=${year}&month=${month}`
  )
  const days = response.data?.days
  return { userId, year, month, days: Array.isArray(days) ? days : [] }
}

// 다른 달로 넘기면 그 달의 기록이 올 때까지 직전에 받은 달의 기록을 그대로 둔다 (같은 사용자의 기록만).
export const userMonthAvailabilityOptions = (userId: number, year: number, month: number) =>
  queryOptions({
    queryKey: QueryCacheKeys.user.getUserMonthAvailability(userId, year, month),
    staleTime: availabilityStaleTime(year, month),
    queryFn: () => fetchUserMonthDays(userId, year, month),
    placeholderData: previous => (previous?.userId === userId ? previous : undefined)
  })

const useQueryGetUserMonthAvailability = (userId: number, year: number, month: number, enabled: boolean) => {
  return useQuery({ ...userMonthAvailabilityOptions(userId, year, month), enabled: enabled && userId > 0 })
}

export default useQueryGetUserMonthAvailability
